// ./src/utils/api/listAllPages.ts
//
// Reads EVERY row of a service's `list(filter, pagination, sortBy)` (one request — see below), for the callers that
// genuinely need the whole set: CSV export, PRINT SHEETS, and — since ROADMAP #118 — every screen that reads a
// whole set on purpose (a picker, a map, a drag-reorder list, a scan console, a widget that counts).
//
// ⚠ PAPER HAS NO LOAD-MORE BUTTON. A screen showing a subset says so: a Load-more button, a scrollbar,
// a filter chip, a total to compare against. A printed sheet capped at 500 rows looks exactly like a
// complete one in the hand, and the host finds out at the door. That is why a print page must never
// pass a literal `pageSize` — guarded by `npm run check-print-sheets`.
//
// ⚠ AND NEITHER DOES A PICKER. #118 found ~140 screen reads that loaded a whole set with `{ page: 1, pageSize: 500 }`
// (200, 2000, 5000 …): a picker, a map, a reorder list, the tracking console's bib lookup. None said so when the cap
// was hit — the set just ended, so the 501st POI could not be reordered and the 5001st runner could not be scanned.
// A whole-set read goes through here (or passes `WHOLE_SET` to a hook), never through a literal page size —
// guarded by `npm run check-whole-set-reads`.
//
// ⚠ ONE REQUEST, NOT A PAGING LOOP (#118 review). This helper first paged in 500s with LIMIT/OFFSET — and the server
// orders only by the caller's sort, with no tie-breaker. Rows created in one request share their `created_at` (the
// gateway stamps one time per request), so 800 tickets generated together, or a wave of runners started together,
// came back in a different order on page 2 than on page 1: a row repeated and another was SKIPPED, with nothing to
// say so. The race console would have lost runners it found under the old single 2000-row read. One query has no
// second page to disagree with — so the whole set is ONE request, asking for one row more than the stop, which is
// also how it tells "exactly the stop" from "more". (Since @sdflc/backend-helpers 1.0.143 (#128) a limited `list()` ends its
// order with the table's id, so pages no longer disagree — but one request is still the whole set, and the cheaper.)
//
// ⚠ EXTRACTED RATHER THAN COPIED. Several services had grown their own `listAll` loop; #118 folded the last twelve in.
// The rules that matter (a failed read is a failure, never the rows that arrived; refuse rather than cut) are easy to
// get subtly different, and a variant that silently returns a partial list is invisible.
import { OpResult, OP_RESULT_CODES } from "@sdflc/api-helpers";

import { Pagination, SortItem } from "./commonApiTypes";

export interface ListAllOptions {
  /**
   * A STATED stop: the caller takes the first `maxRows` and must SAY so when it hits it (a print sheet's
   * `PrintSheetSummary truncatedAt`). Leave it out and there is no stop to state — a set larger than
   * `RUNAWAY_ROWS` is REFUSED instead of cut.
   */
  maxRows?: number;
}

type ListFn = (filter?: any, pagination?: Pagination, sortBy?: SortItem[]) => Promise<OpResult>;

/**
 * The most rows a read with no stated stop returns. ⚠ It REFUSES past this rather than returning the first 50,000: an
 * export, a message audience or a scan console cut short looks exactly like a complete one (#118 — before it, the
 * audience of a message to every guest stopped at 20,000 without a word).
 */
export const RUNAWAY_ROWS = 50000;

/** The refusal's text — exported so the guard can recognise it. */
export const TOO_MANY_ROWS_MESSAGE = `More than ${RUNAWAY_ROWS.toLocaleString("en-US")} records match — narrow it down to read them.`;

/**
 * ROADMAP #118 — the page size that means "the whole set". Pass it where a read used to name a cap: to a hook's
 * `list(filter, { page: 1, pageSize: WHOLE_SET })`, or as a provider's `pageSize={WHOLE_SET}`; the hook reads it
 * through `listPage`, which reads everything.
 *
 * Infinity ON PURPOSE: it survives `pagination?.pageSize || pageSizeRef.current` (0 would fall through to the
 * default page), `items.length === pageSize` is never true so a hook's `hasMore` is false, and if it ever reaches a
 * service unconverted, JSON sends it as `null` — the server's "no limit" — so the read is still whole, never capped.
 */
export const WHOLE_SET = Number.POSITIVE_INFINITY;

/** Whether a pagination asks for the whole set. */
export const isWholeSet = (pagination?: Pagination | null): boolean => pagination?.pageSize === WHOLE_SET;

/**
 * Every row matching `filter`, in ONE request (see the header: a paging loop skipped rows on a tied sort).
 *
 * ⚠ A FAILED READ IS A FAILURE — never an empty or partial list wearing a success code.
 * ⚠ Past a STATED `maxRows` the caller gets the first `maxRows` and says so (`PrintSheetSummary truncatedAt`). With no
 * `maxRows`, more than `RUNAWAY_ROWS` rows is a refusal (`TOO_MANY_ROWS_MESSAGE`), never a silent cut.
 * The result's `total` is the number of rows read — a hook that trusts `result.total` must not see 0.
 */
export const listAllPages = async <T>(
  listFn: ListFn,
  filter?: any,
  sortBy?: SortItem[],
  opts?: ListAllOptions,
): Promise<OpResult> => {
  const stated = opts?.maxRows != null;
  const stop = opts?.maxRows ?? RUNAWAY_ROWS;
  // One more than the stop: the extra row is how an unstated stop knows the set is bigger than it.
  const result = await listFn(filter, { page: 1, pageSize: stated ? stop : stop + 1 }, sortBy);

  if (result.code !== OP_RESULT_CODES.OK) {
    return result;
  }

  const rows = Array.isArray(result.data) ? (result.data as T[]) : [];

  if (!stated && rows.length > stop) {
    return new OpResult().addError("", TOO_MANY_ROWS_MESSAGE, OP_RESULT_CODES.FAILED);
  }

  const all = rows.slice(0, stop);

  return OpResult.ok(all).setTotal(all.length);
};

/**
 * One page — or, for `WHOLE_SET`, the whole set. What an entity hook calls in place of `Service.list`, so a page that
 * needs the whole set (a reorder list, a map) asks the hook for `WHOLE_SET` instead of naming a cap.
 */
export const listPage = (listFn: ListFn, filter?: any, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> =>
  isWholeSet(pagination) ? listAllPages(listFn, filter, sortBy) : listFn(filter, pagination, sortBy);
